/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2024
 **/
import { AclService, Layer4ApiService, Layer7ApiService } from '../../../../shared';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LogService, ModalService, RbacService } from '../../../../../../core/services';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-acl-details',
    templateUrl: './acls-details.component.html',
    styleUrls: ['./acls-details.component.scss']
})
export class AclsDetailsComponent extends BaseComponent {
    /**
     * Store the frontend label.
     */
    public frontendLabel: string;

    /**
     * Store the table rows.
     */
    public tableRows: any[] = [];

    /**
     * Store the search keys for the table.
     */
    public searchKeys: string[];

    /**
     * Store the original order of the ACL ids
     */
    public aclIdsOriginalOrder: string[] = [];

    /**
     * Store the new order of the ACL ids
     */
    public aclIdsNewOrder: string[] = [];

    /**
     * Trigger a refresh of the drag n drop table
     */
    public refresh: boolean = false;

    /**
     * Store the acl rules.
     */
    public aclRules: any[];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the service type i.e layer 4 or layer 7.
     */
    public serviceType: 'layer4' | 'layer7';

    /**
     * Store the frontends.
     * @private
     */
    private frontends: any[];

    /**
     * Store the frontends.
     * @private
     */
    private backends: any[];

    /**
     * Store the frontend id.
     * @private
     */
    private frontendId: string;

    /**
     * Store the backend id.
     * @private
     */
    private backendId: string = '';

    /**
     * Store selected api service.
     * @private
     */
    private selectedApiService: any;

    constructor(
        private aclService: AclService,
        private activatedRoute: ActivatedRoute,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private modalService: ModalService,
        private rbacService: RbacService
    ) {
        super();
        this.setUpVariablesFromRoute();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Add a new rule or edit an existing rule for the given frontend.
     * @param row
     */
    public addEditAcl(row?: any): void {
        if (!row && this.serviceType === 'layer4' && this.tableRows?.length > 0) {
            return;
        }

        const redirectUrl: string = `/cluster-configuration/manage-acls/acls-details/${this.serviceType}/${this.frontendId}`;
        const redirectText: string = 'acl_details_return_to';
        this.aclService.addEditAcl(
            this.frontends, this.backends, this.aclRules, redirectUrl, redirectText, this.backendId, this.frontendId, row, this.serviceType
        );
    }

    /**
     * Update table rows from search term.
     * @param $event
     */
    public updateTableRows($event: any): void {
        this.tableRows = $event;
    }

    /**
     * Helper function to call populate table rows.
     */
    public resetAclOrder(): void {
        this.refresh = true;
        this.aclIdsNewOrder = this.aclIdsOriginalOrder;
        this.setUpPageData();
    }

    /**
     * Submit the new ACL id order.
     */
    public submitAclOrder(): Observable<any> {
        return new Observable((observer) => {
            // @ts-ignore
            this.layer7ApiService.updateFrontend(this.frontendId, { aclIds: this.aclIdsNewOrder })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.aclIdsOriginalOrder = this.aclIdsNewOrder;
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ACLDetailsComponent: updateAclOrder() error updating frontend ', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm delete of acl rule.
     * @param id
     */
    public confirmDeleteACL(id: string): void {
        const confirmationObject: any = {
            title: 'acl_delete',
            message: 'acl_delete_message',
            callBack: this.deleteAcl.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete an acl rule.
     * @param id
     * @private
     */
    private deleteAcl(id: string): Observable<any> {
        return new Observable((observer) => {
            this.selectedApiService.deleteAclRule(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ACLDetailsComponent: deleteAcl() error deleting acl ', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up required global variables from the route.
     * @private
     */
    private setUpVariablesFromRoute(): void {
        this.frontendId = this.activatedRoute.snapshot.params['frontendId'];
        this.serviceType = this.activatedRoute.snapshot.params['serviceType'];
        this.selectedApiService = this.serviceType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            'frontends': this.selectedApiService.getFrontends(),
            'backends': this.selectedApiService.getBackends(),
            'frontend-acls': this.selectedApiService.getFrontendAcls(this.frontendId)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    if (dataGroups['frontends'].length) {
                        this.frontends = dataGroups['frontends'];
                        const frontend = this.frontends.find(item => item.id === this.frontendId);
                        this.frontendLabel = frontend.name;
                        this.aclIdsOriginalOrder = frontend.aclIds;
                        this.aclIdsNewOrder = frontend.aclIds;
                    }

                    this.backends = dataGroups['backends']?.length ? dataGroups['backends'] : [];
                    this.aclRules = dataGroups['frontend-acls']?.length ? dataGroups['frontend-acls'] : [];
                    this.populateTableRows();

                    this.refresh = false;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ACLDetailsComponent: setUpPageData() error fetching data ', error.message);
                }
            });
    }

    /**
     * Populate table rows.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        for (const acl of this.aclRules) {
            this.tableRows.push({
                id: acl.id,
                condition: this.aclService.matchReadable(acl.condition),
                type: this.aclService.conditionTypeReadable(acl.conditionType),
                redirectType: this.aclService.redirectTypeReadable(acl.redirectType),
                redirectLocation: this.aclService.getBackendName(acl, this.backends),
                redirectCode: acl.redirectCode || 'n/a'
            });
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'manage-acls-add-acl': this.getACLRulesAdd(),
            'manage-acl-rules-edit': this.getACLRulesEdit(),
            'manage-acl-rules-delete': [{ path: '/haproxy/acls/:id', methods: ['DELETE'] }],
            'manage-acl-update-config': [{ path: '/haproxy/frontends/:id', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * New acl paths
     * @private
     */
    private getACLRulesAdd(): any[] {
        return [
            { path: '/haproxy/acls', methods: ['GET', 'POST'] },
            { path: '/haproxy/acls/hanging', methods: ['GET'] },
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['GET'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }

    /**
     * Edit acl paths
     * @private
     */
    private getACLRulesEdit(): any[] {
        return [
            { path: '/haproxy/frontends', methods: ['GET'] },
            { path: '/haproxy/frontends/:id', methods: ['PUT'] },
            { path: '/haproxy/backends', methods: ['GET'] },
            { path: '/haproxy/acls', methods: ['GET'] },
            { path: '/haproxy/acls/:id', methods: ['PUT'] },
            { path: '/fips', methods: ['GET'] },
            { path: '/locks', methods: ['GET'] }
        ];
    }
}
